import { FC } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { ComplianceWithCoursesResponseHydrated as ComplianceWithCourses } from "src/api/compliance";

import CourseList from "../../../components/CourseList";
import Spinner from "../../../components/Spinner";
import { CertificateStatusType } from "../../../services/certificateStatus";

interface AllCoursesProps {
  complianceData: ComplianceWithCourses;
  allCourses?: any;
  companyLmsCourses?: any;
  boughtProducts?: any;
  loading?: boolean;
  error?: string | null;
  isCompanyCourse?: boolean;
}

const AllCourses: FC<AllCoursesProps> = ({
  complianceData,
  allCourses,
  companyLmsCourses,
  boughtProducts,
  loading,
  error,
  isCompanyCourse = false,
}) => {
  const { t } = useTranslation();

  // If there's an error
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // If all courses data is loading
  if (loading) {
    return (
      <Spinner
        message={isCompanyCourse ? t("compliance.loading_company_lms_courses") : t("compliance.loading_all_courses")}
      />
    );
  }

  // Determine which data source to use
  const coursesData = isCompanyCourse ? companyLmsCourses : allCourses;

  // If we have courses data
  if (complianceData?.certificateMap && (coursesData || isCompanyCourse)) {
    const { certificateMap } = complianceData;
    const boughtCourses = boughtProducts?.courses || [];

    // For company courses, we need to show ALL available courses (not just ones with certificates)
    // Combine courses from API with certificates from certificateMap
    let courses;
    if (isCompanyCourse) {
      // Company courses come directly from certificateMap, already enriched
      courses = Array.isArray(companyLmsCourses) ? companyLmsCourses : [];
    } else {
      courses = allCourses?.courses || []; // Only Moodle courses, no training
    }

    // No need to filter again for company courses since we already filtered in the extraction above
    const filteredCourses = courses;

    // Enrich courses with certificate data from new API
    const enrichedCourses = filteredCourses.map((product: any) => {
      // Look up certificate in the map (works for both company and regular courses now)
      const certificate = certificateMap[product.id];

      // If certificate exists in Compliance API, use it directly
      if (certificate) {
        const modifiedActions = { ...certificate.actions };

        // Replace refresher/group course ID with the main course ID in playableCourse
        // This is to confuse users and show directly course ID in Course bought button
        if (modifiedActions.playableCourse && modifiedActions.playableCourse.id !== product.id) {
          modifiedActions.playableCourse = {
            ...modifiedActions.playableCourse,
            id: product.id, // Use main course ID instead of refresher ID
          };
        }

        return {
          ...product, // Merge product data (name, price, type from getAvailableProducts)
          ...certificate, // Use certificate data and actions from Compliance API
          actions: modifiedActions,
        };
      }

      // Course not in certificateMap - create minimal entry with manual actions
      const isBought = boughtCourses.includes(product.id);
      const isFree = !product.price || parseFloat(product.price) === 0 || product.price === "Free";
      const isPurchased = isBought && !isFree;

      return {
        id: product.id,
        name: product.name,
        type: product.type,
        price: product.price,
        status: CertificateStatusType.MISSING,
        validPeriod: null,
        visible: true,
        actions: {
          canUploadCertificate: true,
          isReupload: false,
          playableCourse: isBought || isFree ? product : null,
          purchasableCourse: !isBought && !isFree ? product : null,
          downloadableCertificate: null,
          noNewCourseAvailable: false,
          isPendingApproval: false,
          hasHistory: false, // No certificate = no history
          isPurchased,
        },
      };
    });

    // Sort by name
    const sortedCourses = enrichedCourses.sort((a: any, b: any) => {
      return (a.name || "").localeCompare(b.name || "");
    });

    return sortedCourses.length > 0 ? (
      <CourseList courses={sortedCourses} showDescriptions={false} />
    ) : (
      <Typography>
        {isCompanyCourse ? t("compliance.no_company_lms_courses_found") : t("compliance.no_courses_found")}
      </Typography>
    );
  }

  // Fallback if no data available
  return <Typography>{t("compliance.no_course_data_available")}</Typography>;
};

export default AllCourses;
